import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, MoreHorizontalIcon, PlusIcon, ChevronRightIcon, BuildingIcon, UserIcon, LandmarkIcon, SmartphoneIcon, CreditCardIcon, WalletIcon } from 'lucide-react';
import { getTenant } from '../../../theme/tokens';
import { mockSuppliers } from '../mocks/mockSuppliers';
export function SupplierDetail() {
  const {
    t
  } = useTranslation('suppliers');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const supplier = mockSuppliers.find(s => s.id === id) || mockSuppliers[0];
  const fullAddress = [supplier.addressLine1, supplier.addressLine2, supplier.city, supplier.province, supplier.postcode, supplier.country].filter(Boolean).join(', ');
  const billingEmail = supplier.useAsBillingEmail ? supplier.email : `${supplier.email} (separate billing)`;
  const bills = {
    total: supplier.amountOwed !== `${getTenant().currency.code} 0.00` ? 1 : 0,
    due: supplier.amountOwed !== `${getTenant().currency.code} 0.00` ? 1 : 0,
    awaitingApproval: 0,
    scheduled: 0,
    paid: 0
  };
  const history = [{
    id: 'h1',
    action: `${supplier.name} was created`,
    date: supplier.dateCreated.replace(',', ' at')
  }];
  return <div className="space-y-8 max-w-7xl mx-auto pb-12" data-id="element-13275">
      {/* Header */}
      <div className="flex items-center" data-id="element-13276">
        <button onClick={() => navigate('/suppliers')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-13277">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-13278" />
          {t('pages.SupplierDetail.all_suppliers')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-13279">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6" data-id="element-13280">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-13281">
            {/* Top Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-13282">
              <div className="flex justify-between items-start" data-id="element-13283">
                <div data-id="element-13284">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-13285">
                    {supplier.name}
                  </h1>
                  <div className="flex items-center text-sm" data-id="element-13286">
                    <span className="text-gray-500 mr-2" data-id="element-13287">
                      {t('pages.SupplierDetail.you_owe_them')}
                    </span>
                    <span className="px-2.5 py-0.5 bg-[#E2231A] text-white text-xs font-semibold rounded-md shadow-sm" data-id="element-13288">
                      {supplier.amountOwed}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-3" data-id="element-13289">
                  <button onClick={() => navigate(`/bill-form?supplierId=${supplier.id}`)} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-13290">
                    {t('pages.SupplierDetail.add_bill')}
                  </button>
                  <button aria-label="More actions" className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-13291">
                    <MoreHorizontalIcon size={20} aria-hidden="true" data-id="element-13292" />
                  </button>
                </div>
              </div>
            </div>

            {/* Details Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-13293">
              <div className="flex justify-between items-center mb-6" data-id="element-13294">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-13295">
                  {t('pages.SupplierDetail.details')}
                </h3>
                <button onClick={() => navigate(`/supplier-form?id=${supplier.id}`)} className="text-sm font-medium text-[#9F140F] hover:text-[#8C6C00]" data-id="element-13296">
                  {t('pages.SupplierDetail.edit')}
                </button>
              </div>

              <div className="space-y-4" data-id="element-13297">
                {/* Type */}
                <div className="grid grid-cols-3 gap-4 border-b border-gray-50 pb-4" data-id="element-13298">
                  <div className="text-sm text-gray-500" data-id="element-13299">
                    {t('pages.BulkSupplierUpload.type')}
                  </div>
                  <div className="col-span-2" data-id="element-13300">
                    {supplier.supplierType === 'company' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-13301">
                        <BuildingIcon size={12} className="mr-1.5" data-id="element-13302" />
                        {t('pages.SupplierDetail.company')}
                      </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-13303">
                        <UserIcon size={12} className="mr-1.5" data-id="element-13304" />
                        {t('pages.SupplierDetail.individual')}
                      </span>}
                  </div>
                </div>

                {/* Company-specific fields */}
                {supplier.supplierType === 'company' && <>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13305">
                      <div className="text-sm text-gray-500" data-id="element-13306">
                        {t('pages.SupplierDetail.company_name_local')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13307">
                        {supplier.companyNameTh || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13308">
                      <div className="text-sm text-gray-500" data-id="element-13309">
                        {t('pages.SupplierDetail.company_name_english')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13310">
                        {supplier.companyNameEn || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13311">
                      <div className="text-sm text-gray-500" data-id="element-13312">
                        {t('pages.SupplierDetail.tax_id_registration_no')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900 font-mono" data-id="element-13313">
                        {supplier.taxId || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13314">
                      <div className="text-sm text-gray-500" data-id="element-13315">
                        {t('pages.SupplierDetail.branch')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13316">
                        {supplier.branch === '00000' ? '00000 (Head Office)' : supplier.branch || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13317">
                      <div className="text-sm text-gray-500" data-id="element-13318">
                        {t('pages.SupplierDetail.vat_registered')}
                      </div>
                      <div className="col-span-2" data-id="element-13319">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${supplier.vatRegistered ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-13320">
                          {supplier.vatRegistered ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                    {supplier.contactPerson && <div className="grid grid-cols-3 gap-4" data-id="element-13321">
                        <div className="text-sm text-gray-500" data-id="element-13322">
                          {t('pages.SupplierDetail.contact_person')}
                        </div>
                        <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13323">
                          {supplier.contactPerson}
                        </div>
                      </div>}
                  </>}

                {/* Individual-specific fields */}
                {supplier.supplierType === 'individual' && <>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13324">
                      <div className="text-sm text-gray-500" data-id="element-13325">
                        {t('pages.SupplierDetail.full_name_thai')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13326">
                        {supplier.fullNameTh || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13327">
                      <div className="text-sm text-gray-500" data-id="element-13328">
                        {t('pages.SupplierDetail.full_name_english')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13329">
                        {supplier.fullNameEn || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13330">
                      <div className="text-sm text-gray-500" data-id="element-13331">
                        {t('pages.SupplierDetail.id_type')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900 capitalize" data-id="element-13332">
                        {supplier.idType === 'citizen_id' ? 'Citizen ID' : supplier.idType === 'passport' ? 'Passport' : '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13333">
                      <div className="text-sm text-gray-500" data-id="element-13334">
                        {t('pages.SupplierDetail.id_number')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900 font-mono" data-id="element-13335">
                        {supplier.idNumber || '—'}
                      </div>
                    </div>
                    {supplier.individualTaxId && <div className="grid grid-cols-3 gap-4" data-id="element-13336">
                        <div className="text-sm text-gray-500" data-id="element-13337">
                          {t('pages.BulkSupplierUpload.tax_id')}
                        </div>
                        <div className="col-span-2 text-sm font-medium text-gray-900 font-mono" data-id="element-13338">
                          {supplier.individualTaxId}
                        </div>
                      </div>}
                  </>}

                {/* Shared fields */}
                <div className="grid grid-cols-3 gap-4 border-t border-gray-50 pt-4" data-id="element-13339">
                  <div className="text-sm text-gray-500" data-id="element-13340">
                    {t('pages.BulkSupplierUpload.email')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13341">
                    {supplier.email}
                  </div>
                </div>
                {supplier.phone && <div className="grid grid-cols-3 gap-4" data-id="element-13342">
                    <div className="text-sm text-gray-500" data-id="element-13343">
                      {t('pages.BulkSupplierUpload.phone')}
                    </div>
                    <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13344">
                      {supplier.phone}
                    </div>
                  </div>}
                <div className="grid grid-cols-3 gap-4" data-id="element-13345">
                  <div className="text-sm text-gray-500" data-id="element-13346">
                    {t('pages.SupplierDetail.billing_email')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13347">
                    {billingEmail}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4" data-id="element-13348">
                  <div className="text-sm text-gray-500" data-id="element-13349">
                    {t('pages.SupplierDetail.address')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13350">
                    {fullAddress || '—'}
                  </div>
                </div>
                {supplier.internalReference && <div className="grid grid-cols-3 gap-4" data-id="element-13351">
                    <div className="text-sm text-gray-500" data-id="element-13352">
                      {t('pages.SupplierDetail.internal_reference')}
                    </div>
                    <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13353">
                      {supplier.internalReference}
                    </div>
                  </div>}
              </div>
            </div>

            {/* Pay-in Method Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-13354">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-13355">
                {t('pages.SupplierDetail.payin_method')}
              </h3>

              <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-13356">
                <div className="flex items-center" data-id="element-13357">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${supplier.payInMethod === 'acquiring' ? 'bg-green-100' : 'bg-blue-100'}`} data-id="element-13358">
                    {supplier.payInMethod === 'acquiring' ? <WalletIcon size={20} className="text-green-700" data-id="element-13359" /> : <CreditCardIcon size={20} className="text-blue-700" data-id="element-13360" />}
                  </div>
                  <div data-id="element-13361">
                    <div className="text-sm font-semibold text-gray-900" data-id="element-13362">
                      {supplier.payInMethod === 'acquiring' ? 'Acquiring Volume' : 'Card Payment'}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-13363">
                      {supplier.payInMethod === 'acquiring' ? 'Funded from merchant acquiring settlement balance' : 'Charged to linked card'}
                    </div>
                  </div>
                </div>

                {supplier.payInMethod === 'card' && supplier.cardNumber && <div className="grid grid-cols-2 gap-y-3 gap-x-6 mt-4 pt-4 border-t border-gray-200" data-id="element-13364">
                    <div data-id="element-13365">
                      <div className="text-xs text-gray-500 mb-1" data-id="element-13366">
                        {t('pages.SupplierDetail.cardholder')}
                      </div>
                      <div className="text-sm font-medium text-gray-900" data-id="element-13367">
                        {supplier.cardholderName}
                      </div>
                    </div>
                    <div data-id="element-13368">
                      <div className="text-xs text-gray-500 mb-1" data-id="element-13369">
                        {t('pages.SupplierDetail.card_number')}
                      </div>
                      <div className="text-sm font-medium text-gray-900 font-mono" data-id="element-13370">
                        {supplier.cardNumber}
                      </div>
                    </div>
                  </div>}
              </div>
            </div>

            {/* Payout Method Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-13371">
              <div className="flex justify-between items-center mb-6" data-id="element-13372">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-13373">
                  {t('pages.SupplierDetail.payout_method')}
                </h3>
                <button onClick={() => navigate(`/supplier-form?id=${supplier.id}`)} className="text-sm font-medium text-[#9F140F] hover:text-[#8C6C00]" data-id="element-13374">
                  {t('pages.SupplierDetail.edit_payout_method')}
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-13375">
                {supplier.payoutMethod === 'bank_account' ? <>
                    <div className="flex items-center mb-4" data-id="element-13376">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-4" data-id="element-13377">
                        <LandmarkIcon size={20} className="text-green-700" data-id="element-13378" />
                      </div>
                      <div data-id="element-13379">
                        <div className="text-sm font-semibold text-gray-900" data-id="element-13380">
                          {t('pages.SupplierDetail.bank_account')}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-13381">
                          {t('pages.SupplierDetail.direct_transfer')}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-6 mt-4 pt-4 border-t border-gray-200" data-id="element-13382">
                      <div data-id="element-13383">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-13384">
                          {t('pages.SupplierDetail.bank_name')}
                        </div>
                        <div className="text-sm font-medium text-gray-900" data-id="element-13385">
                          {supplier.bankName}
                        </div>
                      </div>
                      <div data-id="element-13386">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-13387">
                          {t('pages.SupplierDetail.account_number')}
                        </div>
                        <div className="text-sm font-medium text-gray-900 font-mono" data-id="element-13388">
                          {'***' + supplier.accountNumber.slice(-4)}
                        </div>
                      </div>
                      <div data-id="element-13389">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-13390">
                          {t('pages.SupplierDetail.account_name')}
                        </div>
                        <div className="text-sm font-medium text-gray-900" data-id="element-13391">
                          {supplier.accountName}
                        </div>
                      </div>
                    </div>
                  </> : <>
                    <div className="flex items-center mb-4" data-id="element-13392">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-4" data-id="element-13393">
                        <SmartphoneIcon size={20} className="text-purple-700" data-id="element-13394" />
                      </div>
                      <div data-id="element-13395">
                        <div className="text-sm font-semibold text-gray-900" data-id="element-13396">
                          {t('pages.SupplierDetail.promptpay')}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-13397">
                          {supplier.promptPayType === 'phone' ? 'Phone number' : supplier.promptPayType === 'citizen_id' ? 'Citizen ID' : 'Tax ID'}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-6 mt-4 pt-4 border-t border-gray-200" data-id="element-13398">
                      <div data-id="element-13399">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-13400">
                          {t('pages.SupplierDetail.promptpay_id')}
                        </div>
                        <div className="text-sm font-medium text-gray-900 font-mono" data-id="element-13401">
                          {'***' + supplier.promptPayId.slice(-4)}
                        </div>
                      </div>
                      <div data-id="element-13402">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-13403">
                          {t('pages.SupplierDetail.display_name')}
                        </div>
                        <div className="text-sm font-medium text-gray-900" data-id="element-13404">
                          {supplier.promptPayName}
                        </div>
                      </div>
                    </div>
                  </>}
              </div>
            </div>

            {/* Source Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-13405">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-13406">
                {t('pages.SupplierDetail.source')}
              </h3>
              <div className="space-y-4" data-id="element-13407">
                <div className="grid grid-cols-3 gap-4" data-id="element-13408">
                  <div className="text-sm text-gray-500" data-id="element-13409">
                    {t('pages.SupplierDetail.platform')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13410">
                    {supplier.source}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4" data-id="element-13411">
                  <div className="text-sm text-gray-500" data-id="element-13412">
                    {t('pages.SupplierDetail.date_created')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-13413">
                    {supplier.dateCreated}
                  </div>
                </div>
              </div>
            </div>

            {/* Bills Section */}
            <div className="p-8" data-id="element-13414">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4" data-id="element-13415">
                {t('pages.SupplierDetail.bills')}
              </h3>
              <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors group" data-id="element-13416">
                <div data-id="element-13417">
                  <div className="text-sm font-medium text-gray-900 mb-1" data-id="element-13418">
                    {bills.total} {t('pages.SupplierDetail.bill')}
                    {bills.total !== 1 ? 's' : ''}{' '}
                    {t('pages.SupplierDetail.for')} {supplier.name}
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-13419">
                    {bills.due} {t('pages.SupplierDetail.due')}{' '}
                    {bills.awaitingApproval}{' '}
                    {t('pages.SupplierDetail.awaiting_approval')}{' '}
                    {bills.scheduled} {t('pages.SupplierDetail.scheduled_and')}{' '}
                    {bills.paid} {t('pages.SupplierDetail.paid')}
                  </div>
                </div>
                <ChevronRightIcon size={20} className="text-gray-400 group-hover:text-gray-600 transition-colors" data-id="element-13420" />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - History */}
        <div className="lg:col-span-1" data-id="element-13421">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-4" data-id="element-13422">
            {t('pages.SupplierDetail.history')}
          </h3>
          <div className="space-y-6" data-id="element-13423">
            {history.map((event, index) => <div key={event.id} className="relative flex gap-4" data-id="element-13424">
                {index !== history.length - 1 && <div className="absolute left-[9px] top-6 bottom-[-24px] w-px bg-gray-200" data-id="element-13425"></div>}
                <div className="relative z-10 flex-shrink-0 w-5 h-5 bg-gray-100 rounded flex items-center justify-center mt-0.5" data-id="element-13426">
                  <PlusIcon size={12} className="text-gray-600" data-id="element-13427" />
                </div>
                <div data-id="element-13428">
                  <p className="text-sm font-medium text-gray-900" data-id="element-13429">
                    {event.action}
                  </p>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-13430">{event.date}</p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}